import React from 'react';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Folder as AllFoldersIcon,
  Add as AddIcon,
} from '@mui/icons-material';
// Same sidebar shell (.leftSidebar is supplied by the parent) and .menuItem /
// .menuThumb / .menuContent card look the Videos and Photo albums tabs use,
// so Shared's folder list stops looking like a leftover from the old UI.
// V2-only — the classic /your-videos page keeps using the original
// Pages/private/videos/components/SharedSidebar (old CategoriesSidebar look),
// which still matches its own (not yet redesigned) Videos/Albums sidebar.
import layoutStyles from '../../content-layout-details/index.module.css';

// Derive a short status label for non-active links (shown next to the name).
const getLinkStatusLabel = (link) => {
  if (link.is_active === '0' || link.is_active === 0 || link.is_active === false) {
    return { text: 'Revoked', color: '#ef4444' };
  }
  if (link.is_expired) {
    return { text: 'Expired', color: '#f59e0b' };
  }
  return null;
};

export const SharedSidebarV2 = ({
  links = [],
  selectedLink,
  onLinkSelect,
  loading = false,
  folderCovers = {},
  onCreateFolder,
}) => {
  const handleLinkClick = (link) => {
    onLinkSelect(link);
  };

  const handleAllFoldersClick = () => {
    onLinkSelect(null);
  };

  return (
    <>
      <div className={layoutStyles.sidebarHeader}>
        <h2 className={layoutStyles.sidebarTitle}>Shared Folders</h2>
      </div>

      {loading ? (
        <div className={layoutStyles.sidebarSkeleton}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={layoutStyles.skeletonMenuItem}>
              <div className={layoutStyles.skeletonIcon} />
              <div className={layoutStyles.skeletonText} />
            </div>
          ))}
        </div>
      ) : (
        <div className={layoutStyles.menuList}>
          {/* All Folders — same pinned-first-row treatment as "All Videos". */}
          <div
            className={`${layoutStyles.menuItem} ${!selectedLink ? layoutStyles.active : ''}`}
            onClick={handleAllFoldersClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleAllFoldersClick();
              }
            }}
          >
            <span className={`${layoutStyles.menuThumb} ${layoutStyles.menuThumbIcon}`}>
              <AllFoldersIcon />
            </span>
            <div className={layoutStyles.menuContent}>
              <span className={layoutStyles.menuLabel}>All Folders</span>
              {links.length > 0 && (
                <span className={layoutStyles.menuSub}>{`${links.length} ${links.length === 1 ? 'folder' : 'folders'}`}</span>
              )}
            </div>
          </div>

          {/* Folder (Link) Items */}
          {links.length === 0 ? (
            <div className={layoutStyles.sidebarEmptyState}>
              <FolderIcon />
              <p>No folders found</p>
              {onCreateFolder && (
                <button
                  type="button"
                  className={layoutStyles.sidebarEmptyCreateBtn}
                  onClick={onCreateFolder}
                >
                  <AddIcon />
                  Create folder
                </button>
              )}
            </div>
          ) : (
            links.map((link, index) => {
              const isSelected = selectedLink?.uuid && selectedLink?.uuid === link.uuid;
              const displayName = link.label || 'Untitled Folder';
              const status = getLinkStatusLabel(link);
              const contributionCount = Number(link.contribution_count) || 0;
              const cover = folderCovers?.[link.uuid];
              return (
                <div
                  key={link.uuid || link.id || index}
                  className={`${layoutStyles.menuItem} ${isSelected ? layoutStyles.active : ''}`}
                  onClick={() => handleLinkClick(link)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleLinkClick(link);
                    }
                  }}
                >
                  {cover ? (
                    <span className={layoutStyles.menuThumb} style={{ backgroundImage: `url(${cover})` }} />
                  ) : (
                    <span className={`${layoutStyles.menuThumb} ${layoutStyles.menuThumbIcon}`}>
                      {isSelected ? <FolderOpenIcon className={layoutStyles.menuIcon} /> : <FolderIcon className={layoutStyles.menuIcon} />}
                    </span>
                  )}
                  <div className={layoutStyles.menuContent}>
                    <span className={layoutStyles.menuLabel}>{displayName}</span>
                    <span
                      className={layoutStyles.menuSub}
                      style={status ? { color: status.color, fontWeight: 700 } : undefined}
                    >
                      {status ? status.text : `${contributionCount} ${contributionCount === 1 ? 'item' : 'items'}`}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </>
  );
};

export default SharedSidebarV2;
